import { Entity } from '@core/entities/entity'
import { plainToInstance, Type } from 'class-transformer'
import { User } from '@core/entities/user'
import { IPermitTemplateRole } from './IPermitTemplateRole'
import { localizedStrings } from '@core/strings'

const PERMISSION_TYPES = [
  'PERIODIC_CHECK_SUBMIT',
  'ADD_PARTICIPANT',
  'POST_PERMIT_CHECK_SUBMIT',
  'DECLARE_WORK_COMPLETE',
  'REQUEST_EXTENSION',
  'DO_PRE_START_TASKS',
] as const

const CHECK_IN_PERMISSION_TYPES = [
  'SELF_CHECK_IN_OUT',
  'OTHERS_CHECK_IN_OUT',
] as const

export type PermitTemplateRolePermissionType =
  | typeof PERMISSION_TYPES[number]
  | typeof CHECK_IN_PERMISSION_TYPES[number]

export type PermitTemplateStatus = 'UNPUBLISHED' | 'PUBLISHED' | 'ARCHIVED'

export class PermitTemplateRole extends Entity<IPermitTemplateRole> {
  id: number
  templateVersionId: number
  name: string
  description?: string
  briefingText?: string
  permissions: Array<PermitTemplateRolePermissionType>
  transferOwnershipOnBriefing: boolean
  groupIds?: Array<number>
  isBriefingAllowed?: boolean

  @Type(() => User)
  createdBy: User
  createdOn: string
  @Type(() => User)
  modifiedBy: User
  modifiedOn: string

  public hasPermission(permission: PermitTemplateRolePermissionType): boolean {
    return (this.permissions ?? []).includes(permission)
  }

  public canCheckSelfIn(): boolean {
    return this.hasPermission('SELF_CHECK_IN_OUT')
  }

  public canCheckOthersIn(): boolean {
    return this.hasPermission('OTHERS_CHECK_IN_OUT')
  }

  public static getPermissionTypes(options: {
    includeCheckIn: boolean
  }): PermitTemplateRolePermissionType[] {
    return options.includeCheckIn
      ? [...PERMISSION_TYPES, ...CHECK_IN_PERMISSION_TYPES]
      : [...PERMISSION_TYPES]
  }

  public static getPermissionText(
    permission: PermitTemplateRolePermissionType,
  ): string {
    switch (permission) {
      case 'PERIODIC_CHECK_SUBMIT':
        return localizedStrings.submitPeriodicChecks
      case 'ADD_PARTICIPANT':
        return localizedStrings.addParticipants
      case 'POST_PERMIT_CHECK_SUBMIT':
        return localizedStrings.submitPostPermitChecks
      case 'DECLARE_WORK_COMPLETE':
        return localizedStrings.declareWorkComplete
      case 'REQUEST_EXTENSION':
        return localizedStrings.requestExtensions
      case 'DO_PRE_START_TASKS':
        return localizedStrings.doPreStartTasks
      case 'SELF_CHECK_IN_OUT':
        return localizedStrings.checkSelfInOut
      case 'OTHERS_CHECK_IN_OUT':
        return localizedStrings.checkOthersInOut
      default:
        return ''
    }
  }

  public static new(payload: unknown): PermitTemplateRole {
    const entity = plainToInstance(PermitTemplateRole, payload)

    return entity
  }

  // Maps a list of roles to a `{ roleId: roleName }` lookup, for resolving
  // role names by id when displaying participants.
  public static toNameById(
    roles: Pick<IPermitTemplateRole, 'id' | 'name'>[] = [],
  ): Record<number, string> {
    return Object.fromEntries(roles.map((role) => [role.id, role.name]))
  }
}
